//*************************************************************************************
//* WaMS Component Framework Autocomplete - app.autocomplete.js
//* Version 0.12
//* Author: Bret Huisenga
//* Component framework autocomplete plugin
//* Requires jQuery, WaMS Component Framework
//*
//* 7/07/22 Updates keypress event handler
//*************************************************************************************
import $ from 'jquery';
import { util } from './app.js';

// Variable to hold autocomplete objects — module-scoped (no longer global)
const AutocompleteObjects = {};

function Autocomplete(e, options) {
    this.el = $(e);
    this.id = $(this.el).attr("id");

    //Define properties for the object
    Object.defineProperties(this,
        {
            //active item, find the element in the result list with the autocomplete-item and active classes
            "activeItem": {
                "get": function () { return this.resultList.find(".autocomplete-item.active"); }
            },
            //result items, find all of the elements in the result list with the autocomplete-item class
            "resultItems": {
                "get": function () { return this.resultList.find(".autocomplete-item"); }
            },
            //search text, get the value of the object's search box
            "searchText": {
                "get": function () { return this.searchBox.val(); }
            }
        });
    //generate a random id for the search
    this.searchIndex = this.generateId();

    //default options configuration
    this.options = {
        cacheResults: true,
        endPoint: "",
        initialSelected: null,
        initialText: "",
        onSelect: function (selectedItem) { },
        outputField: "",
        placeholder: "Start typing to search...",
        searchMinLength: 3,
        replaceVal: true,
        template: "<div class='display-text'>#= FirstName # #= LastName #</div><div class='autocomplete-item-subtext'>#= NetID #</div>",
        filterType: "server",
        label: "",
        propertyName: "",
        multiSelect: false,
        multiSelectDelimiter: "|",
        multiSelectMaxCount: null
    };

    //merge the options with the user configured options
    this.options = $.extend({}, this.options, options);

    //populate data attribute options
    if (this.el.data("autocomplete-cacheresults") !== "" && this.el.data("autocomplete-cacheresults") !== undefined) {
        this.options.cacheResults = this.el.data("autocomplete-cacheresults");
    }
    if (this.el.data("autocomplete-endpoint")) {
        this.options.endPoint = this.el.data("autocomplete-endpoint");
    }
    if (this.el.data("autocomplete-initialtext")) {
        this.options.initialText = this.el.data("autocomplete-initialtext");
    }
    if (this.el.data("autocomplete-outputfield")) {
        this.options.outputField = this.el.data("autocomplete-outputfield");
    }
    if (this.el.data("autocomplete-placeholder") !== "undefined") {
        this.options.placeholder = this.el.data("autocomplete-placeholder");
    }
    if (this.el.data("autocomplete-searchminlength") !== "" && this.el.data("autocomplete-searchminlength") !== undefined) {
        this.options.searchMinLength = this.el.data("autocomplete-searchminlength");
    }
    if (this.el.data("autocomplete-replaceval") !== "" && this.el.data("autocomplete-replaceval") !== undefined) {
        this.options.replaceVal = this.el.data("autocomplete-replaceval");
    }
    if (this.el.data("autocomplete-template")) {
        this.options.template = this.el.data("autocomplete-template");
    }
    if (this.el.data("autocomplete-additionaldataselector")) {
        this.options.additionaldataselector = this.el.data("autocomplete-additionaldataselector");
    }
    if (this.el.data("autocomplete-filtertype")) {
        this.options.filterType = this.el.data("autocomplete-filtertype");
    }
    if (this.el.attr('aria-label')) {
        this.options.label = this.el.attr('aria-label');
    }
    if (this.el.data("autocomplete-property-name")) {
        util.console.log(`setting property name: ${this.el.data("autocomplete-property-name")}`);
        this.options.propertyName = this.el.data("autocomplete-property-name");
    }
    if (this.el.data("autocomplete-multiselect") !== "" && this.el.data("autocomplete-multiselect") !== undefined) {
        this.options.multiSelect = this.el.data("autocomplete-multiselect");
    }
    if (this.el.data("autocomplete-multiselect-delimiter")) {
        this.options.multiSelectDelimiter = this.el.data("autocomplete-multiselect-delimiter");
    }
    if (this.el.data("autocomplete-multiselect-max-count")) {
        this.options.multiSelectMaxCount = this.el.data("autocomplete-multiselect-max-count");
    }
    // plugin implementation
    this.selected = null;
    this.outputText = null;
    this.displayText = null;
    this.cache = {};

    //init new object
    this.init();

    //Log item
    AutocompleteObjects[this.searchIndex] = this;
}

Autocomplete.prototype.init = function () {
    // check if input and alert if not
    if (!this.el.is("input")) {
        util.console.error("Error: The autocomplete feature can only be used on inputs with type set to text.");
        return;
    }
    //Log the searchIndex
    this.el.attr("data-searchIndex", this.searchIndex);

    //init
    this.container = $("<div>");
    this.container.addClass("autocomplete-container");

    this.el.after(this.container);
    //add multiselect container
    this.resultsBox = $("<div>");
    this.resultsBox.addClass("autocomplete-selected-container");
    this.container.append(this.resultsBox);
    //create search box
    this.searchBox = $("<input>");
    // set accessibility tags
    this.searchBox.attr("type", "text");
    this.searchBox.attr("placeholder", this.options.placeholder);
    this.searchBox.attr("role", "combobox");
    this.searchBox.attr("aria-autocomplete", "both");
    this.searchBox.attr("aria-haspopup", "true");
    this.searchBox.attr("aria-owns", "search-" + this.searchIndex + "-results");
    this.searchBox.attr("aria-expanded", "false");
    var searchLabel = this.options.label || this.options.placeholder;
    if (searchLabel) { this.searchBox.attr("aria-label", searchLabel); }
    this.searchBox.attr("id", "search-" + this.searchIndex);
    this.searchBox.addClass("form-control");

    this.container.append(this.searchBox);

    //add announcement for notifying screen readers of changes
    this.announcement = $("<div>");
    this.announcement.addClass("autocomplete-announcement");
    this.announcement.attr("aria-live", "assertive");

    this.container.append(this.announcement);

    this.resultList = $('<div id="search-' +
        this.searchIndex +
        '-results" class="autocomplete-results-list" role="listbox"></div>');
    this.container.append(this.resultList);
    this.el.addClass("autocomplete-hide");
    this.el.attr("tabindex", -1);

    //Bind actions
    //Bind Focus
    this.searchBox.on("focus", $.proxy(this.onFocus, this));
    // close drop down when clicking away
    this.searchBox.on("blur", $.proxy(this.onBlur, this));
    // this is mostly navigation support
    this.searchBox.on("keyup", $.proxy(this.onKeyPress, this));
    // bind search
    this.searchBox.on("input propertychange paste", $.proxy(this.onInput, this));

    // stop all default behavior when the user hits enter, up arrow and down arrow
    this.searchBox.on("keydown keypress",
        function (event) {
            //up and down are IE/Edge specific values
            var keysToCancel = ["Enter", "Up", "ArrowUp", "Down", "ArrowDown"];
            //up arrow moves active up
            if (keysToCancel.includes(event.key)) {
                event.preventDefault();
            }
        });


    // Bind click event to list elements in results
    this.resultList.on("mousedown", "div.autocomplete-item", $.proxy(this.selectItem, this));
    this.resultList.on({

        mousemove: function () {
            $(this).siblings().removeClass("active");
            $(this).addClass("active");
        },
        mouseleave: function (e) {
            $(this).removeClass("active");
        }
    },
        "div.autocomplete-item");

    //init selected value
    if (this.options.multiSelect) {

        if (this.el.val()) {
            var jsonResult;
            try {
                jsonResult = JSON.parse(this.el.val());
            }
            catch (e) {
                util.console.error(`Error parsing Multiselect JSON: ${e}`);
            };
            if (jsonResult) {
                var autoComplete = this;
                $.each(jsonResult, function (i, item) {
                    autoComplete.setValue(item.DisplayText, item.OutputText);
                });
            }
        }
    } else {
        if (this.options.initialSelected !== null) {

            this.activeItem = this.populateTemplate(this.initialSelected);
            this.selectItem();

        } else if (this.options.initialText !== "") {
            var outputText = this.el.val();
            if (!outputText) {
                outputText = this.options.initialText;
            }
            this.setValue(this.options.initialText, outputText);
        } else if (this.el.val()) {
            this.setValue(this.el.val(), this.el.val());
        }
    }
};

Autocomplete.prototype.onBlur = function () {
    this.emptyResultList();
    if (this.selected === null) {
        if (this.options.replaceVal) {
            this.searchBox.val("");

            // trigger the change on the text box in case anyone is listening
            this.el.trigger("change");
        } else {
            this.el.val(this.searchText);
        }
    }
};
Autocomplete.prototype.onFocus = function () {
    if (this.searchText.length === 0 && this.options.searchMinLength === 0) {
        this.search();
    }
};
Autocomplete.prototype.onKeyPress = function (event) {
    //reset selected
    var keyID = event.key;
    util.console.log("keypress:" + keyID);
    // handle keyboard controls
    switch (event.key) {
        case "Escape":
            // ESC closes list
            this.hideResultList();
            break;
        case "Enter":
            // Enter selects active item
            if (this.activeItem.length)
                this.selectItem();
            break;
        case "ArrowUp":
        case "Up":
            // up arrow moves active up
            this.navigateUp();
            break;
        case "ArrowDown":
        case "Down":
            // down arrow moves active down
            this.navigateDown();
            break;
    }
};
Autocomplete.prototype.onInput = function (event) {
    //reset selected
    this.selected = {};
    var keyID = event.key;
    util.console.log("change:" + keyID);
    // Check if search should happen
    if (this.searchText.length >= this.options.searchMinLength) {
        this.search();
    }
    //Handle backspace/delete so results don't remain with less than specified characters
    else if (this.resultList.length > 0) {
        this.resetSearchList();
    }
};
Autocomplete.prototype.search = function () {
    this.resetSearchList();

    // show loading image and notify users
    this.announcement.text("Loading results.");

    // check if we've searched for this term before
    if (this.options.cacheResults && this.searchText in this.cache) {
        this.populateList(this.cache[this.searchText]);
    }
    else {
        if (this.options.filterType === "client") {
            if (this.clientSideDataOptions === undefined) {
                //get the values from somewhere
                this.getClientSideResults($.proxy(this.filterClientSideResults, this));
            } else {
                //filter
                //set
                this.filterClientSideResults();
            }
        }
        else {
            // only add spinner if one doesn't already exist
            if (!$(this.container).find('.loading-spinner-container').length) {
                const spinnerHtml =
                    `<div class="d-flex justify-content-center loading-spinner-container">
                        <div class="spinner-border text-primary m-5" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>`;

                $(this.container).append(spinnerHtml);
            }

            //Check if we've searched for this term before
            clearTimeout(this.delayTimer);
            this.delayTimer = setTimeout($.proxy(function () {
                this.getResults();
            }, this), 500);
        }
    }
};
Autocomplete.prototype.getResults = function () {
    var data = { searchText: this.searchText };

    var customData = util.getCustomData(this.el);
    if (customData) {
        data = { ...data, ...customData };
    }
    if (this.options.additionaldataselector !== undefined && $(this.options.additionaldataselector).length) {
        var formData = util.getFormValues($(this.options.additionaldataselector));
        data = { ...data, ...formData }
    }
    if (this.request != null) {
        this.request.abort();
        this.request = null;
    }
    this.request = $.ajax({
        url: window.applicationPath + this.options.endPoint,
        data: data,
        dataType: "json",
        context: this,
        error: function (xhr, status, error) {
            // Remove spinner on error
            $(this.container).find('.loading-spinner-container').remove();
            window.app.page.handleAjaxError(xhr, status, error);
        }
    }).done(function (data) {
        // Remove spinner on success
        $(this.container).find('.loading-spinner-container').remove();

        //Add results to cache
        this.cache[this.searchText] = data;

        //add results to shown list
        this.populateList(data);
    });
};
Autocomplete.prototype.getClientSideResults = function (callback) {
    this.request = $
        .ajax({
            url: window.applicationPath + this.options.endPoint,
            dataType: "json",
            context: this,
            error: function(xhr, status, error) { window.app.page.handleAjaxError(xhr, status, error); }
        }).done(function (data) {
            // Remove spinner
            $(this.container).find('.loading-spinner-container').remove();

            //Add results to cache
            this.clientSideDataOptions = data;
            if (callback) {
                callback();
            }
        });
}
Autocomplete.prototype.filterClientSideResults = function() {
    var filteredData = [];
    var cleanSearch = this.searchText.trim().toLowerCase();

    if (this.clientSideDataOptions.length > 0) {
        var keys = Object.keys(this.clientSideDataOptions[0]);

        //For each client side option get the index
        for (var index in this.clientSideDataOptions) {
            //find the object
            var result = this.clientSideDataOptions[index];
            if (cleanSearch.length > 0) {
                //for each search key
                for (var i = 0; i < keys.length; i++) {
                    //if the filtered data list does not have the object
                    if (filteredData.indexOf(result) === -1) {
                        var propName = keys[i];
                        //if there is a search text
                        //only add items that match
                        if (result[propName].toString().trim().toLowerCase().indexOf(cleanSearch) !== -1) {
                            filteredData.push(result);
                        }
                    }
                }
            } else {
                //add all item
                filteredData.push(result);
            }
        }
    }
    //Add results to cache
    this.cache[this.searchText] = filteredData;
    //add results to shown list
    this.populateList(filteredData);
};
Autocomplete.prototype.populateList = function (results) {
    this.emptyResultList();
    //update accessibility announcement
    if (results.length > 0) {
        this.announcement.text(results.length + " records found, use up and down arrows to review");
    } else {
        this.announcement.text(results.length + " records found.");
    }

    //Add results to the list
    for (var i = 0; i < results.length; i++) {
        util.console.log(this.populateTemplate(results[i]));
        //populate template
        var populatedTemplate = this.populateTemplate(results[i]);
        this.resultList.append("<div id='search-" +
            this.searchIndex +
            "-result-" +
            i +
            "' class='autocomplete-item' role='option' data-item-data='" +
            JSON.stringify(results[i]).replace(/[']/g, "&#39;").replace(/["]/g, "&#34;") +
            "'>" +
            populatedTemplate +
            "</div>");
    }
    if (results.length === 1) {
        this.resultList.append("<div class='autocomplete-message'>Showing top record.</div>");
    } else if (results.length > 0) {
        this.resultList.append("<div class='autocomplete-message'>Showing top " + results.length + " records.</div>");
    } else {
        this.resultList.append("<div class='autocomplete-message'>No records found.</div>");
    }
    this.showResultList();
};

//Populate the template with the data provided
Autocomplete.prototype.populateTemplate = function (result) {
    util.console.log(result);
    var personOption = this.options.template;
    //Loop through each key in the list, and replace with the identifier
    for (var key in result) {
        const re = new RegExp("#=[ ]*" + key + "[ ]*#", "gi");
        personOption = personOption.replace(re, result[key]);
    }

    //Remove all missing identifiers
    const cleanupRe = new RegExp("#=.*?#", "g");
    personOption = personOption.replace(cleanupRe, "");

    //Add the output field
    if (this.options.outputField !== "" && typeof result[this.options.outputField] !== "undefined") {
        personOption += '<input type="hidden" class="output-text" value="' +
            result[this.options.outputField] +
            '"/>';
    }
    return personOption;
};

Autocomplete.prototype.selectItem = function () {
    var displayText = this.activeItem.find(".display-text").text().trim();

    //output selected value
    var outputText = this.activeItem.find(".output-text").val();

    var itemData = this.activeItem.data("item-data");

    this.setValue(displayText, outputText, itemData);

};
Autocomplete.prototype.UpdateSelectedValues = function() {
    if (this.options.multiSelect) {
        var objects = new Array();
        $.each($(".autocomplete-multiselect-data", this.resultsBox),
            function(i, item) {
                objects.push({
                    outputText: $(".autocomplete-multiselect-outputtext", item).val(),
                    displayText: $(".autocomplete-multiselect-displaytext", item).val()
                });
            });
        var results = objects.length === 0 ? null : JSON.stringify(objects);
        this.el.val(results);

        this.checkMaxCountStatus();
    }
};
Autocomplete.prototype.setValue = function (displayText, outputText, itemData) {
    if (itemData) {
        this.selected = itemData;
    } else {
        this.selected = { displayText: displayText, outputText: outputText };
    }
    if (this.options.multiSelect) {

        var newItem = $('<span class="autocomplete-selected-item"></span>').text(displayText || outputText);
        var identifier = this.generateId();
        var propertyName = this.options.propertyName;
        var removeButton =
            $(`<button class="btn bg-uw-teal text-white ms-2 autocomplete-remove" aria-label="Unselect ${displayText}"><span aria-hidden="true">&times;</span></button>`);
        newItem.append(removeButton);
        var hiddenValueWrapper = $("<div>", { class: "autocomplete-multiselect-data" });
        newItem.append(hiddenValueWrapper);

        var index = $(`<input type="hidden" class="autocomplete-multiselect-identifier" name="${propertyName}.Index" value="${identifier}"/>`);
        hiddenValueWrapper.append(index);
        var value = $(`<input type="hidden" class="autocomplete-multiselect-outputtext" name="${propertyName}[${identifier}].OutputText" value="${outputText}"/>`);
        hiddenValueWrapper.append(value);
        var displayText = $(
            `<input type="hidden" class="autocomplete-multiselect-displaytext" name="${propertyName}[${identifier}].DisplayText" value="${displayText}"/>`);
        hiddenValueWrapper.append(displayText);
        var itemDataField = $(
            `<input type="hidden" class="autocomplete-multiselect-itemdata" name="${propertyName}[${identifier}].ItemData" value="${itemData}"/>`);
        hiddenValueWrapper.append(itemDataField);



        this.resultsBox.append(newItem);
        var updateFunction = $.proxy(this.UpdateSelectedValues, this);
        removeButton.click(function (e) {
            e.preventDefault();
            newItem.remove();
            updateFunction();
        });

        this.searchBox.val("");
        this.UpdateSelectedValues();
    } else {

        this.searchBox.val(displayText);
        if (outputText) {
            //output selected value
            this.el.val(outputText);
        } else {
            //output display value
            this.el.val(displayText);
        }
    }

    this.emptyResultList();
    this.options.onSelect.call(this.selected);

    $(document).trigger("component-autocomplete-selected", this);
    $(document).trigger("component-autocomplete-selected-" + this.id, this);
    util.console.log(`component-autocomplete-selected triggered: ${this.id}`);

    //trigger the change on the text box in case anyone is listening
    this.el.trigger("change");
};

Autocomplete.prototype.clearSelectItem = function () {
    if (this.options.replaceVal) {
        this.el.val("");
    }
    this.selected = null;
    this.emptyResultList();
    this.options.onSelect.call(this.selected);

    $(document).trigger("component-autocomplete-selectcleared", this);
    $(document).trigger("component-autocomplete-selectcleared-" + this.id, this);

    $(document).trigger("component-autocomplete-selected", this);
    $(document).trigger("component-autocomplete-selected-" + this.id, this);
};

Autocomplete.prototype.resetSearchList = function () {
    this.emptyResultList();
    this.clearSelectItem();
};

Autocomplete.prototype.showResultList = function () {
    this.resultList.addClass("active");
    this.resultList.scrollTop(0);
    this.searchBox.attr("aria-expanded", "true");

    // Remove spinner
    $(this.container).find('.loading-spinner-container').remove();
};
Autocomplete.prototype.hideResultList = function () {
    this.resultList.removeClass("active");
    this.searchBox.attr("aria-expanded", "false");
};
Autocomplete.prototype.emptyResultList = function () {
    this.hideResultList();
    this.resultList.empty();
};

Autocomplete.prototype.navigateUp = function () {
    if (this.activeItem.length) {
        var index = this.resultItems.index(this.activeItem);
        this.activeItem.removeClass("active");
        this.activeItem.removeAttr("aria-selected");
        var newActiveIndex = index - 1;
        if (index > -1) {
            this.resultItems.eq(newActiveIndex).addClass("active");
            this.resultItems.eq(newActiveIndex).attr("aria-selected", "true");
            this.searchBox.attr("aria-activedescendant", this.resultItems.eq(newActiveIndex).attr("id"));
            this.resultItems.eq(newActiveIndex)[0].scrollIntoView({ block: "nearest", inline: "start" });

        }
    } else {
        // set the first element active
        this.resultItems.first().addClass("active");
        this.resultItems.first().attr("aria-selected", "true");
        this.searchBox.attr("aria-activedescendant", this.resultItems.first().attr("id"));
        this.resultList.scrollTop(0);
    }
};
Autocomplete.prototype.navigateDown = function () {
    if (this.activeItem.length && this.resultItems.index(this.activeItem) < this.resultItems.length - 1) {
        var index = this.resultItems.index(this.activeItem);
        this.activeItem.removeClass("active");
        this.activeItem.removeAttr("aria-selected");
        var newActiveIndex = index + 1;
        if (index < this.resultItems.length) {
            this.resultItems.eq(newActiveIndex).addClass("active");
            this.resultItems.eq(newActiveIndex).attr("aria-selected", "true");
            this.searchBox.attr("aria-activedescendant", this.resultItems.eq(newActiveIndex).attr("id"));
            this.resultItems.eq(newActiveIndex)[0].scrollIntoView({ block: "nearest", inline: "start" });

        }
    } else {
        //set the first element active
        this.resultItems.first().addClass("active");
        this.resultItems.first().siblings().removeClass("active");
        this.resultItems.first().attr("aria-selected", "true");
        this.searchBox.attr("aria-activedescendant", this.resultItems.first().attr("id"));
        this.resultList.scrollTop(0);
    }
};

Autocomplete.prototype.checkMaxCountStatus = function () {
    if (!this.options.multiSelect) {
        return;
    }

    var currentCount = this.resultsBox.find('.autocomplete-selected-item').length;
    var maxCount = this.options.multiSelectMaxCount;

    // If maxCount is null, zero, or undefined, do nothing (unlimited)
    if (maxCount === null || maxCount === undefined || maxCount <= 0) {
        this.searchBox.prop('disabled', false);
        this.searchBox.attr('placeholder', this.options.placeholder);
        return;
    }

    if (currentCount >= maxCount) {
        // Disable the search box when max count is reached
        this.searchBox.prop('disabled', true);
        this.searchBox.attr('placeholder', `Maximum of ${maxCount} items selected`);
        this.searchBox.val('');
        this.emptyResultList();
    } else {
        // Re-enable the search box if below max count
        this.searchBox.prop('disabled', false);
        this.searchBox.attr('placeholder', this.options.placeholder);
    }
};
Autocomplete.prototype.generateId = function () {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g,
        function (c) {
            var r = Math.random() * 16 | 0, v = c == "x" ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
};

Autocomplete.BindSetButtons = function (el) {
    $('[data-autocomplete-action="set"]', el).on("click",
        function () {
            var action = $(this);
            $("#" + action.data("autocomplete-target")).AutoComplete().setValue(action.data("autocomplete-set-text"), action.data("autocomplete-set-value"));
        });
};

// Installs $.fn.AutoComplete and $.fn.IsAutoComplete onto jQuery — called once from main.js
export function installAutocompletePlugin() {
    $.fn.AutoComplete = function () {
        var searchIndex = $(this[0]).data("searchindex");
        var autocompleteObject;
        if (searchIndex === undefined || searchIndex === "") {
            autocompleteObject = new Autocomplete($(this));
        }
        else {
            autocompleteObject = AutocompleteObjects[searchIndex];
        }

        return autocompleteObject;
    };
    $.fn.IsAutoComplete = function () {
        var searchIndex = $(this[0]).data("searchindex");
        if (searchIndex === undefined || searchIndex === "") {
            return false;
        }
        else {
            return true;
        }
    };
}

export default Autocomplete;
